import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Clock,
  Edit2,
  FileText,
  Loader2,
  Mail,
  Pause,
  RefreshCcw,
  Save,
} from 'lucide-react';
import TriggerStep from './components/Trigger';
import SettingsStep from './components/Settings';
import GoalAndExit from './components/GoalAndExit';
import WorkflowStep from './components/WorkflowStep';
import ReviewStep from './components/Review';
import CampaignDashboard from '../../components/workflow/CampaignDashboard';
import { cn } from '../../utils/cn';
import { useDispatch } from 'react-redux';
import { getAttributesRequest } from 'store/attributes';
import {
  getCampaignRequest,
  updateCampaignRequest,
  publishCampaignRequest,
  clearCampaignState,
  clearPublishCampaignState,
  reviewCampaignRequest,
} from 'store/campaigns';
import {
  selectCampaignSuccess,
  selectPublishCampaignLoading,
  selectPublishCampaignSuccess,
  selectReviews,
} from 'store/campaigns/campaigns.selector';
import { getSegmentsRequest } from 'store/segments';
import {
  getWorkflowByCampaignRequest,
  selectSelectedWorkflow,
  selectWorkflowsError,
  updateWorkflowRequest,
  clearWorkflowState,
} from 'store/workflows';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { useToast } from 'contexts/ToastContext';
import { Condition, WorkflowNode } from 'types';
import { CampaignBuilderSkeleton } from 'skeletons';
import { v4 as uuidv4 } from 'uuid';
import { validateConditions } from 'utils/conditionValidation';

type CampaignStep = 1 | 2 | 3 | 4 | 5;

const STEPS = [
  { number: 1, label: 'Trigger', key: 'trigger' },
  { number: 2, label: 'Settings', key: 'settings' },
  { number: 3, label: 'Goal & Exit', key: 'goalAndExit' },
  { number: 4, label: 'Workflow', key: 'workflow' },
  { number: 5, label: 'Review', key: 'review' },
];

const CampaignBuilder: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const openWorkflow = Boolean(
    (location.state as { openWorkflow?: boolean } | null)?.openWorkflow
  );
  const openWorkflowApplied = useRef(false);
  const dispatch = useDispatch<any>();
  const [currentStep, setCurrentStep] = useState<CampaignStep>(1);
  const [campaignName, setCampaignName] = useState<string>('-NA-');
  const [isEditingName, setIsEditingName] = useState(false);
  const [selectedTrigger, setSelectedTrigger] = useState<string | null>(null);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [conditions, setConditions] = useState<{ [key: string]: Condition[] }>(
    {}
  );
  const [event, setEvent] = useState<string>('');
  const [filters, setFilters] = useState<{ [key: string]: Condition[] }>({});
  const [selectedCampaignType, setSelectedCampaignType] = useState<
    'all_contacts' | 'future_contacts'
  >('all_contacts');
  const [goal, setGoal] = useState<{
    type: string; // goal|no goal
    condition: Object;
    timeDuration: {
      duration: number;
      unit: string;
      event: string;
    };
  }>({
    type: 'goal',
    condition: {},
    timeDuration: {
      duration: 1,
      unit: 'week',
      event: 'being sent',
    },
  });
  const [exitSurvey, setExitSurvey] = useState<string>('goal_or_trigger');
  const [isGoalValid, setIsGoalValid] = useState<boolean>(true);
  // State for managing view modes
  const [viewMode, setViewMode] = useState<'dashboard' | 'editor'>('editor');
  const campaign = useSelector(
    (state: RootState) => state.campaigns.selectedCampaign
  );
  const loading = useSelector((state: RootState) => state.campaigns.loading);
  const error = useSelector((state: RootState) => state.campaigns.error);
  const workflow = useSelector(selectSelectedWorkflow);
  const reviews = useSelector(selectReviews);
  const campaignSuccess = useSelector(selectCampaignSuccess);
  const publishSuccess = useSelector(selectPublishCampaignSuccess);
  const workflowError = useSelector(selectWorkflowsError);
  const publishCampaignLoading = useSelector(selectPublishCampaignLoading);

  const { showToast } = useToast();

  useEffect(() => {
    if (id) {
      dispatch(getCampaignRequest(id));
      dispatch(getAttributesRequest());
      dispatch(getSegmentsRequest({}));
      dispatch(getWorkflowByCampaignRequest(id));
      dispatch(reviewCampaignRequest(id));
    }
  }, [id, dispatch]);

  // Live and paused campaigns open the dashboard. Returning from email
  // content should land on the workflow step instead.
  useEffect(() => {
    if (!campaign) return;
    if (openWorkflow && !openWorkflowApplied.current) {
      openWorkflowApplied.current = true;
      setViewMode('editor');
      setCurrentStep(4);
      return;
    }
    if (openWorkflowApplied.current) return;
    if (campaign.status === 'live' || campaign.status === 'paused') {
      setViewMode('dashboard');
    } else {
      setViewMode('editor');
    }
  }, [campaign, openWorkflow]);

  useEffect(() => {
    if (campaign && workflow) {
      setCampaignName(campaign.name);
      const trigger: WorkflowNode | undefined = workflow.draftFlow.nodes.find(
        (node: any) => node.type === 'trigger'
      );
      if (trigger) {
        setConditions({ ...trigger.data.config?.conditions });
        setFilters({ ...trigger.data.config?.filters });
        setSelectedTrigger(trigger.data.config?.trigger || null);
        setEvent(trigger.data.config?.event ?? campaign?.event ?? '');
        const goal = campaign.goal;
        setGoal({
          type: goal?.type,
          condition: goal?.condition,
          timeDuration: goal?.timeDuration,
        });
        setExitSurvey(campaign.exitSurvey || 'goal_or_trigger');
        if (completedSteps && completedSteps.size === 0 && currentStep === 1) {
          if (workflow.status === 'live' || campaign?.status === 'paused') {
            setCompletedSteps(new Set([1, 2, 3, 4, 5]));
            setCurrentStep(4);
          } else if (workflow.draftFlow.nodes.length > 1) {
            setCompletedSteps(new Set([1, 2, 3, 4]));
            setCurrentStep(4);
          } else if (workflow.draftFlow.nodes.length === 0) {
            setCompletedSteps(new Set([]));
            setCurrentStep(4);
          }
        }
      }
    }
  }, [workflow, campaign, dispatch, completedSteps, currentStep]);

  // Show toast when campaign is updated successfully
  useEffect(() => {
    if (campaignSuccess) {
      showToast({
        type: 'success',
        title: 'Success',
        message: 'Campaign updated successfully!',
      });
      dispatch(clearCampaignState());
    }
  }, [campaignSuccess, showToast, dispatch]);

  // Handle publish success and refresh campaign data
  useEffect(() => {
    if (publishSuccess && id) {
      showToast({
        type: 'success',
        title: 'Campaign is live now!',
        message: 'Campaign is live now!',
      });
      dispatch(getCampaignRequest(id));
      dispatch(clearPublishCampaignState());
      setViewMode('dashboard');
    }
  }, [publishSuccess, id, dispatch, showToast]);

  // Show toast when there's a workflow error
  useEffect(() => {
    if (workflowError) {
      showToast({
        type: 'error',
        title: 'Error',
        message: workflowError,
      });
      dispatch(clearWorkflowState());
    }
  }, [workflowError, showToast, dispatch]);

  const handleStepClick = (step: number) => {
    const completedStepsArray = Array.from(completedSteps);
    if (step <= currentStep || completedStepsArray.includes(step)) {
      setCurrentStep(step as CampaignStep);
    }
  };

  const isValidCondition = (conditions: { [key: string]: Condition[] }) => {
    const validation = validateConditions(conditions);
    return validation.isValid;
  };

  const isEventTriggerConditionsValid = (
    conds: { [key: string]: Condition[] },
    trigger: string | null
  ) => {
    if (trigger !== 'event') return isValidCondition(conds);
    const allEmpty = Object.values(conds).every(
      (arr) => !Array.isArray(arr) || arr.length === 0
    );
    if (allEmpty) return true;
    return isValidCondition(conds);
  };

  const isTriggerStepValid = () => {
    if (!selectedTrigger?.trim()) return false;
    if (selectedTrigger === 'event' && !event?.trim()) return false;
    return isEventTriggerConditionsValid(conditions, selectedTrigger);
  };

  const handleSave = () => {
    if (currentStep === 1) {
      if (!selectedTrigger?.trim()) {
        showToast({
          type: 'error',
          title: 'Trigger required',
          message: 'Please select a trigger before continuing.',
        });
        return;
      }
      if (selectedTrigger === 'event' && !event?.trim()) {
        showToast({
          type: 'error',
          title: 'Event required',
          message:
            'Please select an event for the event trigger before continuing.',
        });
        return;
      }
      const isValid = isEventTriggerConditionsValid(
        conditions,
        selectedTrigger
      );
      if (!isValid) {
        showToast({
          type: 'error',
          title: 'Invalid conditions',
          message: 'Please fix the errors in the conditions before saving.',
        });
        return;
      }
      handleSaveTrigger();
    }

    if (currentStep === 2) {
      const conditionType = Object.keys(filters)[0];
      const conditions = filters[conditionType];
      const isEmpty = conditions.every(
        (condition: Condition) =>
          !condition.field && !condition.operator && !condition.value
      );
      const isValid = isValidCondition(filters);
      if (!isEmpty && !isValid) {
        showToast({
          type: 'error',
          title: 'Invalid filters',
          message: 'Please fix the errors in the filters before saving.',
        });
        return;
      }
      handleSaveFilters();
    }

    if (currentStep === 3) {
      console.log('Goal data:', goal);
      console.log('Exit Survey:', exitSurvey);
      handleSaveGoalAndExit();
    }

    if (currentStep < 5) {
      setCompletedSteps((prev) => {
        const prevArray = Array.from(prev);
        return new Set([...prevArray, currentStep]);
      });
      setCurrentStep((prev) => (prev + 1) as CampaignStep);
    }

    if (currentStep === 5) {
      handleStartCampaign();
    }
  };

  const handleStartCampaign = (
    selectedType?: 'all_contacts' | 'future_contacts'
  ) => {
    if (!id) return;
    const effectiveTriggerType = selectedTrigger || campaign?.triggerType;
    if (effectiveTriggerType === 'event') {
      dispatch(publishCampaignRequest(id));
      return;
    }
    const typeToUse = selectedType || selectedCampaignType;
    dispatch(publishCampaignRequest(id, typeToUse));
  };

  const handleSwitchToDashboard = useCallback(
    (screen: 'dashboard' | 'editor') => {
      setViewMode(screen);
    },
    []
  );

  const getStatusText = (status: string) => {
    switch (status) {
      case 'live':
        return 'Running';
      case 'paused':
        return 'Paused';
      case 'archived':
        return 'Archived';
      default:
        return 'Draft';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'live':
        return <CheckCircle2 className="w-4 h-4 text-success" />;
      case 'paused':
        return <Pause className="w-4 h-4 text-warning" />;
      case 'archived':
        return <Clock className="w-4 h-4 text-muted-foreground" />;
      default:
        return <FileText className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleSaveTrigger = () => {
    const triggerNode = workflow?.draftFlow.nodes.find(
      (node: any) => node.type === 'trigger'
    );
    const draftFlow = workflow?.draftFlow;
    if (draftFlow && triggerNode) {
      const updatedDraftFlow = {
        ...draftFlow,
        nodes: draftFlow.nodes.map((node: any) => {
          if (node.id === triggerNode.id) {
            return {
              ...node,
              data: {
                ...node.data,
                config: {
                  ...node.data.config,
                  trigger: selectedTrigger,
                  event: event ?? '',
                  conditions,
                  frequency: node.data.config?.frequency || 'one_time',
                },
              },
            };
          }
          return node;
        }),
      };
      dispatch(
        updateWorkflowRequest({
          campaignId: id || '',
          draftFlow: updatedDraftFlow,
        })
      );
    } else {
      const triggerNode = {
        id: uuidv4(),
        type: 'trigger',
        position: { x: 0, y: 0 },
        data: {
          type: 'trigger',
          isFixed: true,
          isDefault: true,
          config: {
            trigger: selectedTrigger,
            event: event ?? '',
            conditions,
            filters,
            frequency: 'one_time',
          },
        },
        draggable: false,
        deletable: false,
      };
      dispatch(
        updateWorkflowRequest({
          campaignId: id || '',
          draftFlow: {
            nodes: [triggerNode],
            edges: [],
          },
        })
      );
    }
  };

  const handleSaveFilters = () => {
    const triggerNode = workflow?.draftFlow.nodes.find(
      (node: any) => node.type === 'trigger'
    );
    const draftFlow = workflow?.draftFlow;
    if (draftFlow && triggerNode) {
      const updatedDraftFlow = {
        ...draftFlow,
        nodes: draftFlow.nodes.map((node: any) => {
          if (node.id === triggerNode.id) {
            return {
              ...node,
              data: {
                ...node.data,
                config: { ...node.data.config, filters: filters },
              },
            };
          }
          return node;
        }),
      };
      dispatch(
        updateWorkflowRequest({
          campaignId: id || '',
          draftFlow: updatedDraftFlow,
        })
      );
    }
  };

  const handleSaveGoalAndExit = () => {
    if (!id) return;
    dispatch(
      updateCampaignRequest(id, {
        goal: goal,
        exitSurvey: exitSurvey,
      })
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <TriggerStep
            triggerType={campaign?.triggerType || ''}
            event={event}
            onEventChange={setEvent}
            selectedTrigger={selectedTrigger}
            onTriggerSelect={setSelectedTrigger}
            conditions={conditions}
            setConditions={setConditions}
            campaignStatus={campaign?.status || 'draft'}
          />
        );
      case 2:
        return (
          <SettingsStep
            filters={filters}
            setFilters={setFilters}
            campaignStatus={campaign?.status || 'draft'}
          />
        );
      case 3:
        return (
          <GoalAndExit
            goal={goal}
            exitSurvey={exitSurvey}
            onGoalChange={setGoal}
            onExitSurveyChange={setExitSurvey}
            onValidationChange={setIsGoalValid}
          />
        );
      case 4:
        return <WorkflowStep />;
      case 5:
        return (
          <ReviewStep
            campaign={campaign || undefined}
            workflow={workflow}
            conditions={conditions}
            onSelectedTypeChange={setSelectedCampaignType}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  // Show skeleton while loading
  if (loading) {
    return <CampaignBuilderSkeleton />;
  }

  // Show error message if there's an error
  if (error) {
    return (
      <div className="min-h-[calc(100vh-100px)] bg-background flex items-center justify-center">
        <div className="bg-card rounded-lg shadow-sm border border-destructive/30 p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-destructive-background rounded-full mb-4">
              <AlertCircle className="w-6 h-6 text-destructive" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Error Loading Campaign
            </h3>
            <p className="text-sm text-destructive mb-6">{error}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => id && dispatch(getCampaignRequest(id))}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors text-sm font-medium"
              >
                Retry
              </button>
              <button
                onClick={() => navigate('/campaigns')}
                className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm font-medium"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-100px)] w-full bg-background relative">
      {/* Campaign Status Header - shown when campaign is live */}
      {(campaign?.status === 'live' || campaign?.status === 'paused') && (
        <div className="bg-card border-b border-border px-6 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-primary-foreground" />
                </div>
                <div className="flex gap-2">
                  <h1 className="text-lg font-bold text-card-foreground">
                    {campaign.name}
                  </h1>
                  <div className="flex items-center space-x-2 mt-1">
                    <div
                      className={cn(
                        'flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium',
                        campaign.status === 'live'
                          ? 'bg-success-background text-success'
                          : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {getStatusIcon(campaign.status)}
                      <span>{getStatusText(campaign.status)}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">•</span>
                    <span className="text-sm text-muted-foreground">
                      Last saved {formatDate(campaign.updatedAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleSwitchToDashboard('dashboard')}
                  className={cn(
                    'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    viewMode === 'dashboard'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  )}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => handleSwitchToDashboard('editor')}
                  className={cn(
                    'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    viewMode === 'editor'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  )}
                >
                  Edit Campaign
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {viewMode === 'dashboard' &&
      (campaign?.status === 'live' || campaign?.status === 'paused') ? (
        <CampaignDashboard
          className="h-[calc(95vh-100px)]"
          campaign={campaign}
          onEditWorkflow={() => handleSwitchToDashboard('editor')}
          onPauseCampaign={() => {
            // TODO: Implement pause campaign functionality
            console.log('Pause campaign');
          }}
          onResumeCampaign={() => {
            // TODO: Implement resume campaign functionality
            console.log('Resume campaign');
          }}
        />
      ) : (
        <>
          {/* Top Navigation Bar */}
          <div className="bg-card border-b border-border sticky top-0">
            <div className="max-w-full mx-auto px-6 py-4">
              <div className="flex items-center justify-between">
                {/* Campaign Name */}
                <div className="flex items-center gap-2">
                  {isEditingName ? (
                    <input
                      type="text"
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      onBlur={() => {
                        dispatch(
                          updateCampaignRequest(id || '', {
                            name: campaignName,
                          })
                        );
                        setIsEditingName(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          dispatch(
                            updateCampaignRequest(id || '', {
                              name: campaignName,
                            })
                          );
                          setIsEditingName(false);
                        }
                      }}
                      className="text-lg font-semibold text-foreground border-b-2 border-primary focus:outline-0 px-1 bg-transparent"
                      autoFocus
                    />
                  ) : (
                    <>
                      <h1 className="text-lg font-semibold text-foreground">
                        {campaignName}
                      </h1>
                      <button
                        onClick={() => setIsEditingName(true)}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>

                {/* Step Indicator */}
                <div className="hidden lg:flex items-center flex-1 justify-center px-4 gap-4">
                  {STEPS.map((step, index) => {
                    const completedStepsArray = Array.from(completedSteps);
                    const isCompleted = completedStepsArray.includes(
                      step.number
                    );
                    const isCurrent = currentStep === step.number;
                    const isAccessible =
                      step.number <= currentStep || isCompleted;

                    return (
                      <React.Fragment key={step.number}>
                        <button
                          onClick={() => handleStepClick(step.number)}
                          className={`relative flex items-center ${isAccessible ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                          disabled={!isAccessible}
                        >
                          <div className="flex items-center gap-2 px-2.5">
                            <div
                              className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${
                                isCompleted
                                  ? 'bg-success border-success'
                                  : isCurrent
                                    ? 'bg-card border-primary'
                                    : 'bg-card border-border'
                              }`}
                            >
                              {isCompleted ? (
                                <Check
                                  className="w-4 h-4 text-success-foreground"
                                  strokeWidth={3}
                                />
                              ) : (
                                <span
                                  className={`text-sm font-semibold ${
                                    isCurrent
                                      ? 'text-primary'
                                      : 'text-muted-foreground'
                                  }`}
                                >
                                  {step.number}
                                </span>
                              )}
                            </div>
                            <span
                              className={`text-sm font-medium whitespace-nowrap ${
                                isCurrent
                                  ? 'text-foreground'
                                  : isCompleted
                                    ? 'text-muted-foreground'
                                    : 'text-muted-foreground'
                              }`}
                            >
                              {step.label}
                            </span>
                            {isCurrent && (
                              <div className="w-full h-0.5 bg-primary rounded-full absolute bottom-0 top-11"></div>
                            )}
                          </div>
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 lg:gap-3">
                  <button
                    onClick={() => {
                      navigate('/campaigns');
                    }}
                    className="flex gap-2 items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors border border-border bg-card text-foreground hover:bg-muted focus:outline-0"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Exit
                  </button>
                  {currentStep < 5 ? (
                    <button
                      onClick={handleSave}
                      disabled={
                        (currentStep === 1 && !isTriggerStepValid()) ||
                        (currentStep === 3 && !isGoalValid) ||
                        (currentStep === 4 && !reviews?.isValid)
                      }
                      className="flex gap-2 items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors border border-border bg-card text-foreground hover:bg-muted focus:outline-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {currentStep < 4 && <Save className="w-4 h-4" />}
                      {currentStep < 4 ? 'Save & Next' : 'Next'}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : null}
                  {currentStep === 5 ? (
                    <button
                      onClick={handleSave}
                      disabled={publishCampaignLoading || !reviews?.isValid}
                      className="flex gap-2 items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors border border-primary bg-primary text-primary-foreground hover:bg-primary-hover focus:outline-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {publishCampaignLoading ? (
                        <div className="flex items-center justify-center">
                          <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 text-primary-foreground" />
                          Publishing...
                        </div>
                      ) : campaign?.status === 'live' ? (
                        <>
                          <RefreshCcw className="w-4 h-4" />
                          Update Campaign
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Start Campaign
                        </>
                      )}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          {/* Main Content */}
          <div className="">{renderStepContent()}</div>
        </>
      )}
    </div>
  );
};

export default CampaignBuilder;
